import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { Sidebar } from './Sidebar';
import { BottomNav } from './BottomNav';
import { Topbar } from './Topbar';
import { CommandPalette } from './CommandPalette';
import { UserMenu } from './UserMenu';
import { useEffect, useState } from 'react';

/**
 * Application chrome.
 *  - Desktop/tablet: collapsible sidebar + topbar.
 *  - Mobile: no sidebar, a persistent bottom bar with the five most-used screens.
 *
 * `dir` comes from the ui store, so switching to RTL mirrors the whole layout
 * (the sidebar moves, paddings flip) without touching a single component.
 */
export function AppShell() {
  const user = useAuth((s) => s.user);
  const { sidebarOpen, setSidebarOpen, commandOpen, setCommandOpen } = useUi();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // A route change should close any open overlay.
  const { pathname } = useLocation();
  useEffect(() => {
    setMobileNavOpen(false);
    setProfileOpen(false);
  }, [pathname]);

  if (!user) return null;

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface-0">
      <div className="flex min-h-0 flex-1">
        {/* Desktop sidebar */}
        <Sidebar open={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

        {/* Mobile drawer */}
        {mobileNavOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMobileNavOpen(false)} aria-hidden />
            <div className="absolute inset-y-0 start-0 w-72 animate-fade-in">
              <Sidebar open onToggle={() => setMobileNavOpen(false)} mobile />
            </div>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar
            onOpenMobileNav={() => setMobileNavOpen(true)}
            onOpenPalette={() => setCommandOpen(true)}
            onOpenProfile={() => setProfileOpen((v) => !v)}
            profileOpen={profileOpen}
            onCloseProfile={() => setProfileOpen(false)}
          />

          <main id="main" className="scroll-area min-h-0 flex-1 pb-20 lg:pb-0">
            <div className="mx-auto w-full max-w-[1600px] p-4 sm:p-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* Mobile bottom navigation */}
      <BottomNav />

      <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />
      {profileOpen && <UserMenu onClose={() => setProfileOpen(false)} />}

      {/* Screen-reader landmark for skipping straight to content. */}
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 focus:rounded focus:bg-brand-600 focus:px-3 focus:py-2 focus:text-white">
        Skip to content
      </a>
    </div>
  );
}
